import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { AssigneeSelect, AssigneeBadge } from '../components/AssigneeSelect';
import { 
  LayoutGrid, ListTodo, Kanban, Calendar, Users, Building2, Target, DollarSign,
  HeadphonesIcon, Briefcase, Plus, Search, Filter, RefreshCw, CheckCircle2,
  Clock, AlertTriangle, ChevronRight, MoreHorizontal, Edit, Trash2, Eye,
  ArrowRight, Loader2, FolderKanban, Layers, TrendingUp, AlertCircle, 
  MessageSquare, User, CalendarDays, GripVertical, Bot
} from 'lucide-react';
import api from '../api';

// Department configuration
const departments = [
  { id: 'all', label: 'All Departments', icon: LayoutGrid, color: 'bg-slate-100 text-slate-700' },
  { id: 'crm', label: 'CRM', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { id: 'sales', label: 'Sales', icon: Target, color: 'bg-green-100 text-green-700' },
  { id: 'marketing', label: 'Marketing', icon: Briefcase, color: 'bg-purple-100 text-purple-700' },
  { id: 'finance', label: 'Finance', icon: DollarSign, color: 'bg-amber-100 text-amber-700' },
  { id: 'hr', label: 'HR', icon: Building2, color: 'bg-pink-100 text-pink-700' },
  { id: 'support', label: 'Support', icon: HeadphonesIcon, color: 'bg-cyan-100 text-cyan-700' },
];

const priorityConfig = {
  critical: { label: 'Critical', color: 'bg-red-500 text-white' },
  high: { label: 'High', color: 'bg-orange-500 text-white' },
  medium: { label: 'Medium', color: 'bg-yellow-500 text-white' },
  low: { label: 'Low', color: 'bg-gray-400 text-white' },
};

const statusConfig = {
  todo: { label: 'To Do', color: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  review: { label: 'In Review', color: 'bg-purple-100 text-purple-700' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
};

const projectStatusConfig = {
  planning: { label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  on_hold: { label: 'On Hold', color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

export default function WorkflowManagementPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeDepartment, setActiveDepartment] = useState('all');
  const [activeView, setActiveView] = useState('dashboard');
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [kanbanData, setKanbanData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [workload, setWorkload] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  
  // Dialogs
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Forms
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    department: 'crm',
    project_id: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    status: 'todo',
    tags: '',
    estimated_hours: '',
  });
  
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    department: 'crm',
    status: 'planning',
    priority: 'medium',
    start_date: '',
    end_date: '',
    budget: '',
    team_members: [],
  });

  // Load data
  useEffect(() => {
    loadDashboard();
    loadTasks();
    loadProjects();
    loadTeamMembers();
  }, [activeDepartment]);
  
  useEffect(() => {
    if (activeView === 'kanban') {
      loadKanban();
    }
    if (activeView === 'workload') {
      loadWorkload();
    }
  }, [activeView, activeDepartment]);
  
  const loadDashboard = async () => {
    try {
      const res = await api.get('/workflow-management/dashboard', {
        params: { department: activeDepartment }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTasks = async () => {
    try {
      const params = { department: activeDepartment };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      if (searchTerm) params.search = searchTerm;
      
      const res = await api.get('/workflow-management/tasks', { params });
      setTasks(res.data.tasks || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };
  
  const loadProjects = async () => {
    try {
      const res = await api.get('/workflow-management/projects', {
        params: { department: activeDepartment }
      });
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };
  
  const loadKanban = async () => {
    try {
      const res = await api.get(`/workflow-management/kanban/${activeDepartment}`);
      setKanbanData(res.data);
    } catch (error) {
      console.error('Failed to load kanban:', error);
    }
  };
  
  const loadTeamMembers = async () => {
    try {
      const res = await api.get('/users/team');
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Failed to load team:', error);
    }
  };
  
  const loadWorkload = async () => {
    try {
      const res = await api.get('/workflow-management/workload', {
        params: { department: activeDepartment }
      });
      setWorkload(res.data.workload || []);
    } catch (error) {
      console.error('Failed to load workload:', error);
    }
  };
  
  useEffect(() => {
    loadTasks();
  }, [statusFilter, priorityFilter, assigneeFilter, searchTerm]);
  
  // Task CRUD
  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      const data = {
        ...taskForm,
        tags: taskForm.tags ? taskForm.tags.split(',').map(t => t.trim()) : [],
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
        department: taskForm.department || activeDepartment !== 'all' ? (taskForm.department || activeDepartment) : 'crm',
      };
      
      await api.post('/workflow-management/tasks', data);
      toast.success('Task created');
      setShowTaskDialog(false);
      resetTaskForm();
      loadTasks();
      loadDashboard();
      if (activeView === 'kanban') loadKanban();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };
  
  const handleUpdateTask = async (taskId, updates) => {
    try {
      await api.put(`/workflow-management/tasks/${taskId}`, updates);
      toast.success('Task updated');
      loadTasks();
      loadDashboard();
      if (activeView === 'kanban') loadKanban();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await api.delete(`/workflow-management/tasks/${taskId}`);
      toast.success('Task deleted');
      loadTasks();
      loadDashboard();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };
  
  const handleMoveTask = async (taskId, newStatus) => {
    try {
      await api.put('/workflow-management/kanban/move', null, {
        params: { task_id: taskId, new_status: newStatus }
      });
      loadKanban();
      loadDashboard();
    } catch (error) {
      console.error('Failed to move task:', error);
      toast.error('Failed to move task');
    }
  };
  
  // Project CRUD
  const handleCreateProject = async () => {
    if (!projectForm.name) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      const data = {
        ...projectForm,
        budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
        department: projectForm.department || activeDepartment !== 'all' ? (projectForm.department || activeDepartment) : 'crm',
      };
      
      await api.post('/workflow-management/projects', data);
      toast.success('Project created');
      setShowProjectDialog(false);
      resetProjectForm();
      loadProjects();
      loadDashboard();
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };
  
  const handleDeleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project and all its tasks?')) return;
    
    try {
      await api.delete(`/workflow-management/projects/${projectId}`);
      toast.success('Project deleted');
      loadProjects();
      loadDashboard();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };
  
  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      department: activeDepartment !== 'all' ? activeDepartment : 'crm',
      project_id: '',
      assigned_to: '',
      due_date: '',
      priority: 'medium',
      status: 'todo',
      tags: '',
      estimated_hours: '',
    });
    setSelectedTask(null);
  };
  
  const resetProjectForm = () => {
    setProjectForm({
      name: '',
      description: '',
      department: activeDepartment !== 'all' ? activeDepartment : 'crm',
      status: 'planning',
      priority: 'medium',
      start_date: '',
      end_date: '',
      budget: '',
      team_members: [],
    });
    setSelectedProject(null);
  };
  
  const getDepartmentInfo = (deptId) => departments.find(d => d.id === deptId) || departments[0];

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tasks</p>
                <p className="text-2xl font-bold">{dashboard?.stats?.total_tasks || 0}</p>
              </div>
              <ListTodo className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard?.stats?.in_progress_tasks || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-green-600">{dashboard?.stats?.completed_tasks || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{dashboard?.stats?.overdue_tasks || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">My Tasks</p>
                <p className="text-2xl font-bold text-purple-600">{dashboard?.stats?.my_tasks || 0}</p>
              </div>
              <User className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Department Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Department Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {departments.filter(d => d.id !== 'all').map(dept => {
                const stats = dashboard?.department_stats?.[dept.id] || {};
                const Icon = dept.icon;
                const completionRate = stats.tasks > 0 
                  ? Math.round((stats.completed / stats.tasks) * 100) 
                  : 0;
                
                return (
                  <div 
                    key={dept.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => { setActiveDepartment(dept.id); setActiveView('tasks'); }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${dept.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium">{dept.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {stats.tasks || 0} tasks • {stats.active_projects || 0} active projects
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-medium">{completionRate}%</p>
                        <Progress value={completionRate} className="w-20 h-1.5" />
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Priority Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(dashboard?.priority_breakdown || {}).map(([priority, count]) => {
                const config = priorityConfig[priority];
                const total = Object.values(dashboard?.priority_breakdown || {}).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                
                return (
                  <div key={priority} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Badge className={config?.color}>{config?.label}</Badge>
                      </span>
                      <span className="font-medium">{count} tasks ({percentage}%)</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </span>
            <Button size="sm" variant="outline" onClick={() => setActiveView('tasks')}>
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(dashboard?.recent_tasks || []).slice(0, 5).map(task => {
              const deptInfo = getDepartmentInfo(task.department);
              const DeptIcon = deptInfo.icon;
              
              return (
                <div key={task.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={task.status === 'completed'}
                      onCheckedChange={(checked) => 
                        handleUpdateTask(task.id, { status: checked ? 'completed' : 'todo' })
                      }
                    />
                    <div>
                      <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                        {task.title}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <DeptIcon className="h-3 w-3" />
                        <span>{deptInfo.label}</span>
                        {task.due_date && (
                          <>
                            <span>•</span>
                            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge className={priorityConfig[task.priority]?.color}>{task.priority}</Badge>
                </div>
              );
            })}
            {(dashboard?.recent_tasks || []).length === 0 && (
              <p className="text-center text-muted-foreground py-8">No recent tasks</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Task List
  const renderTasks = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            {Object.entries(priorityConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            <SelectItem value="me">Assigned to Me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {teamMembers.map(member => (
              <SelectItem key={member.id} value={member.id}>
                {member.first_name} {member.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => { resetTaskForm(); setShowTaskDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>
      
      {/* Task List */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <div className="divide-y">
              {tasks.map(task => {
                const deptInfo = getDepartmentInfo(task.department);
                const DeptIcon = deptInfo.icon;
                const assignee = teamMembers.find(m => m.id === task.assigned_to);
                
                return (
                  <div key={task.id} className="flex items-center gap-4 p-4 hover:bg-muted/50">
                    <Checkbox 
                      checked={task.status === 'completed'}
                      onCheckedChange={(checked) => 
                        handleUpdateTask(task.id, { status: checked ? 'completed' : 'todo' })
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </p>
                        <Badge className={statusConfig[task.status]?.color} variant="secondary">
                          {statusConfig[task.status]?.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <DeptIcon className="h-3 w-3" />
                          {deptInfo.label}
                        </span>
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {assignee && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {assignee.first_name} {assignee.last_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className={priorityConfig[task.priority]?.color}>{task.priority}</Badge>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          setSelectedTask(task);
                          setTaskForm({
                            ...task,
                            tags: task.tags?.join(', ') || '',
                            estimated_hours: task.estimated_hours?.toString() || '',
                          });
                          setShowTaskDialog(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500"
                        onClick={() => handleDeleteTask(task.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {tasks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <ListTodo className="h-12 w-12 mb-3 opacity-50" />
                  <p>No tasks found</p>
                  <Button className="mt-4" onClick={() => setShowTaskDialog(true)}>
                    Create Your First Task
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  // Render Kanban
  const renderKanban = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {kanbanData?.task_count || 0} tasks
        </p>
        <Button onClick={() => { resetTaskForm(); setShowTaskDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>
      
      <div className="grid grid-cols-5 gap-4 overflow-x-auto">
        {(kanbanData?.columns || []).map(column => (
          <div key={column.id} className="min-w-[280px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                {column.title}
                <Badge variant="secondary">{column.tasks.length}</Badge>
              </h3>
            </div>
            <div className="space-y-2 min-h-[400px] bg-muted/30 rounded-lg p-2">
              {column.tasks.map(task => {
                const deptInfo = getDepartmentInfo(task.department);
                const assignee = teamMembers.find(m => m.id === task.assigned_to);
                
                return (
                  <Card key={task.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">{task.title}</p>
                        <Badge className={priorityConfig[task.priority]?.color} variant="secondary">
                          {task.priority[0].toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className={deptInfo.color}>
                          {deptInfo.label}
                        </Badge>
                        {assignee && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {assignee.first_name}
                          </span>
                        )}
                      </div>
                      {task.due_date && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                      {/* Quick status change */}
                      <div className="flex gap-1 mt-2">
                        {column.id !== 'completed' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-6 text-xs"
                            onClick={() => handleMoveTask(task.id, column.id === 'todo' ? 'in_progress' : 'completed')}
                          >
                            {column.id === 'todo' ? 'Start' : 'Complete'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Projects
  const renderProjects = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {projects.length} projects
        </p>
        <Button onClick={() => { resetProjectForm(); setShowProjectDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(project => {
          const deptInfo = getDepartmentInfo(project.department);
          const DeptIcon = deptInfo.icon;
          const progress = project.task_count > 0 
            ? Math.round((project.completed_task_count / project.task_count) * 100)
            : 0;
          
          return (
            <Card key={project.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${deptInfo.color}`}>
                      <DeptIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{project.name}</CardTitle>
                      <CardDescription>{deptInfo.label}</CardDescription>
                    </div>
                  </div>
                  <Badge className={projectStatusConfig[project.status]?.color}>
                    {projectStatusConfig[project.status]?.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {project.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {project.description}
                  </p>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{project.completed_task_count || 0} / {project.task_count || 0} tasks</span>
                    {project.end_date && (
                      <span>Due: {new Date(project.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <Separator className="my-3" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {project.budget && (
                      <span>${project.budget.toLocaleString()}</span>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500"
                    onClick={() => handleDeleteProject(project.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {projects.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderKanban className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No projects found</p>
              <Button className="mt-4" onClick={() => setShowProjectDialog(true)}>
                Create Your First Project
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  // Render Workload
  const renderWorkload = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Team Workload</CardTitle>
          <CardDescription>Task distribution across team members</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {workload.map(member => (
              <div key={member.user_id} className="flex items-center gap-4 p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{member.user_name}</p>
                      <p className="text-sm text-muted-foreground">{member.role}</p>
                    </div>
                    <Badge 
                      variant={member.capacity_indicator === 'overloaded' ? 'destructive' : 
                               member.capacity_indicator === 'high' ? 'secondary' : 'outline'}
                    >
                      {member.capacity_indicator}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <ListTodo className="h-4 w-4" />
                      {member.task_count} tasks
                    </span>
                    {member.critical_tasks > 0 && (
                      <span className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-4 w-4" />
                        {member.critical_tasks} critical
                      </span>
                    )}
                    {member.overdue_tasks > 0 && (
                      <span className="flex items-center gap-1 text-orange-600">
                        <AlertTriangle className="h-4 w-4" />
                        {member.overdue_tasks} overdue
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-32">
                  <Progress 
                    value={Math.min(member.task_count * 10, 100)} 
                    className="h-2"
                  />
                </div>
              </div>
            ))}
            {workload.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No team members found</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="p-6" data-testid="workflow-management-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Workflow Management</h1>
        <p className="text-muted-foreground">Manage tasks and projects across all departments</p>
      </div>
      
      {/* Department Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {departments.map(dept => {
            const Icon = dept.icon;
            return (
              <Button
                key={dept.id}
                variant={activeDepartment === dept.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveDepartment(dept.id)}
                className="flex items-center gap-2"
                data-testid={`dept-${dept.id}`}
              >
                <Icon className="h-4 w-4" />
                {dept.label}
              </Button>
            );
          })}
        </div>
      </div>
      
      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="space-y-4">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="view-dashboard">
            <LayoutGrid className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="view-tasks">
            <ListTodo className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          <TabsTrigger value="kanban" data-testid="view-kanban">
            <Kanban className="h-4 w-4 mr-2" />
            Kanban
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="view-projects">
            <FolderKanban className="h-4 w-4 mr-2" />
            Projects
          </TabsTrigger>
          <TabsTrigger value="workload" data-testid="view-workload">
            <Users className="h-4 w-4 mr-2" />
            Workload
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="dashboard">{!loading && renderDashboard()}</TabsContent>
        <TabsContent value="tasks">{renderTasks()}</TabsContent>
        <TabsContent value="kanban">{renderKanban()}</TabsContent>
        <TabsContent value="projects">{renderProjects()}</TabsContent>
        <TabsContent value="workload">{renderWorkload()}</TabsContent>
      </Tabs>
      
      {/* Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedTask ? 'Edit Task' : 'Create Task'}</DialogTitle>
            <DialogDescription>
              {selectedTask ? 'Update task details' : 'Create a new task'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select 
                  value={taskForm.department} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, department: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.filter(d => d.id !== 'all').map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <AssigneeSelect
                  value={taskForm.assigned_to}
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
                  includeAI={true}
                  placeholder="Select assignee"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Project (Optional)</Label>
              <Select 
                value={taskForm.project_id || 'none'} 
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={selectedTask ? () => handleUpdateTask(selectedTask.id, taskForm) : handleCreateTask}>
              {selectedTask ? 'Update' : 'Create'} Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedProject ? 'Edit Project' : 'Create Project'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="project-name">Name *</Label>
              <Input
                id="project-name"
                value={projectForm.name}
                onChange={(e) => setProjectForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Project name"
              />
            </div>
            <div>
              <Label htmlFor="project-desc">Description</Label>
              <Textarea
                id="project-desc"
                value={projectForm.description}
                onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select 
                  value={projectForm.department} 
                  onValueChange={(v) => setProjectForm(prev => ({ ...prev, department: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.filter(d => d.id !== 'all').map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={projectForm.priority} 
                  onValueChange={(v) => setProjectForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={projectForm.start_date}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={projectForm.end_date}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Budget ($)</Label>
              <Input
                type="number"
                value={projectForm.budget}
                onChange={(e) => setProjectForm(prev => ({ ...prev, budget: e.target.value }))}
                placeholder="10000"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProject}>
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
